import { Tracker } from '@com-delta-idp/mnx-skymiles-overview-ui';
import {
  AEMErrorMessage,
  checkErrors,
  getEmptyContent,
  getLink,
  isDisplay,
  isError,
  isValidData
} from '@com-delta-idp/mnx-skymiles-shared';
import { ActionType } from '@com-delta-idp/mnx-ui-kit';
import { logError } from '@com-delta-idp/mnx-utils';
import { Tracker as TrackerContent } from '../models/aem/overview.interface';
import {
  FutureActivityDisabled,
  FutureActivityResponse
} from '../models/api/future-activities.interfaces';
import {
  LoyaltyMemberTierLevel,
  Threshold
} from '../models/api/tracker-details.interfaces';
import { StatusTrackerResponse } from '../models/api/tracker.interfaces';
import { getEarnometer } from '../utils/tracker/earnometer.utils';
import { getJourney, getNextTierName } from '../utils/tracker/journey.utils';
import { getLegends } from '../utils/tracker/legends.utils';
import {
  getHeader,
  setInitialData
} from '../utils/tracker/tracker-extended.utils';

export const getTracker = (
  tracker: StatusTrackerResponse | undefined,
  futureActivities: FutureActivityResponse | FutureActivityDisabled | undefined,
  aem: TrackerContent | undefined,
  featureFlag: boolean | undefined,
  errors: (string | undefined)[],
  aemErrorMessages: AEMErrorMessage[] | undefined
): Tracker => {
  try {
    const futureActivityError = checkFutureActivity(errors);
    if (!futureActivityError && checkErrors(errors, aemErrorMessages)) {
      return isError<Tracker>(
        'tracker',
        aemErrorMessages as AEMErrorMessage[],
        featureFlag
      );
    }
    return checkData(
      tracker,
      aem,
      featureFlag,
      futureActivityError,
      futureActivities,
      aemErrorMessages
    );
  } catch (error) {
    logError(error as Error, 'SKYMILES', 'OVERVIEW', 'TRACKER');
    return getEmptyContent<Tracker>('', [], true, true);
  }
};

const checkData = (
  tracker: StatusTrackerResponse | undefined,
  aem: TrackerContent | undefined,
  featureFlag: boolean | undefined,
  futureActivityError: boolean,
  futureActivities: FutureActivityResponse | FutureActivityDisabled | undefined,
  aemErrorMessages: AEMErrorMessage[] | undefined
) => {
  return isValidData<
    string,
    [
      StatusTrackerResponse | undefined,
      TrackerContent | undefined,
      boolean | undefined
    ]
  >('loyaltyMemberId', tracker, aem, featureFlag) &&
    (futureActivityError || futureActivities)
    ? adaptTrackerObject(
        tracker as StatusTrackerResponse,
        aem as TrackerContent,
        futureActivities as FutureActivityResponse | undefined,
        featureFlag as boolean,
        aemErrorMessages as AEMErrorMessage[]
      )
    : isDisplay<Tracker>(featureFlag);
};

const adaptTrackerObject = (
  api: StatusTrackerResponse,
  aem: TrackerContent,
  futureActivities: FutureActivityResponse | undefined,
  featureFlag: boolean,
  aemErrorMessages: AEMErrorMessage[]
): Tracker => {
  const { futureMedallionData, tier, pending, mqdThreshold } = setInitialData(
    futureActivities,
    api
  );
  if (futureMedallionData) {
    return {
      header: getHeader(futureMedallionData as LoyaltyMemberTierLevel, aem),
      earnometer: getEarnometer(mqdThreshold, aem, pending, tier),
      journey: getJourney(
        mqdThreshold as Threshold,
        aem,
        getNextTierName(api.currentLoyaltyMemberMedallionStatus),
        pending
      ),
      legends: getLegends(tier, aem.legends),
      timeFrame: aem.period,
      action: {
        type: 'text' as ActionType,
        link: getLink(aem.actionLabel, '')
      },
      loaded: true,
      disabled: !featureFlag,
      ariaLabel: '',
      educationalTitle: aem.educationalTitle
    };
  } else {
    return getEmptyContent<Tracker>('tracker', aemErrorMessages, true, true);
  }
};

const checkFutureActivity = (errors: (string | undefined)[]): boolean => {
  return !!errors.find((error) => error === 'future-activity');
};
